import {useEffect, useRef} from 'react'
import {useInView, useMotionValue, useSpring} from 'framer-motion'
import {cn} from 'utils/cn'

const format = (n, decimalPlaces) =>
  Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(Number(Number(n).toFixed(decimalPlaces)))

/**
 * Magic UI — Number Ticker (tailwind-v3 branch), ported to JS on
 * framer-motion (the app's install of `motion`).
 * https://magicui.design/docs/components/number-ticker
 *
 * Springs from `startValue` to `value` once scrolled into view, and
 * again whenever `value` changes. Deviations from upstream: the initial
 * render is formatted (upstream prints the raw startValue until the
 * spring's first tick) and the `text-black tracking-wider` defaults are
 * dropped so the caller sets the type.
 */
export function NumberTicker({
  value,
  startValue = 0,
  direction = 'up',
  delay = 0,
  className,
  decimalPlaces = 0,
  ...props
}) {
  const ref = useRef(null)
  const motionValue = useMotionValue(direction === 'down' ? value : startValue)
  const springValue = useSpring(motionValue, {damping: 60, stiffness: 100})
  const isInView = useInView(ref, {once: true, margin: '0px'})

  useEffect(() => {
    if (!isInView) return undefined
    const timer = setTimeout(() => {
      motionValue.set(direction === 'down' ? startValue : value)
    }, delay * 1000)
    return () => clearTimeout(timer)
  }, [motionValue, isInView, delay, value, direction, startValue])

  useEffect(
    () =>
      springValue.on('change', (latest) => {
        if (ref.current) ref.current.textContent = format(latest, decimalPlaces)
      }),
    [springValue, decimalPlaces]
  )

  return (
    <span
      ref={ref}
      className={cn('inline-block tabular-nums', className)}
      {...props}
    >
      {format(direction === 'down' ? value : startValue, decimalPlaces)}
    </span>
  )
}

export default NumberTicker
